import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Checkbox, Flex, Icon, Tag, Text } from '@chakra-ui/react';
import classNames from 'classnames';
import { isNil, kebabCase } from 'lodash';
import React, { type MouseEvent, type MouseEventHandler } from 'react';
import type { NodeApi, NodeRendererProps } from 'react-arborist';

import { formatNodeCount } from '../../utils/formatNodeCount';
import styles from './Node.module.css';
import { TreeTooltip } from './TreeTooltip';
import type { TreeData } from './useTreeApi';
import { copyTextToClipboard } from './utils/copyToClipboard';
import { getDisplayIdFromNodeId } from './utils/idParsing';
import { resolveSuggestedTitles } from './utils/normalizeSuggestedTitles';
import { isDirectMatch, isSuggestedMatch } from './utils/search';

export type NodeData<E = object> = {
  id: string;
  name: string;
  linkedIds?: string[];
  info?: string;
  /** Pre-normalized title suggestions (e.g. selection lists). */
  suggestedTitles?: string[];
  /**
   * Raw `titles` from TaxonomyTree; normalize with `resolveSuggestedTitles` for display/search.
   */
  taxonomyTitles?: string | readonly (string | null)[] | null;
  nodeCount?: number;
  entityInfo?: E;
  disabled?: boolean;
};

export type TNode<T extends object = object> = NodeApi<TreeData<T>>;

export type DrillMode = {
  isEnabled: boolean;
  level: number;
  isRoot?: boolean;
  drillIntoNode: (props: {
    event?: MouseEvent<SVGElement>;
    node: TNode;
    level: number;
  }) => void;
  drillOutToLevel?: (level: number) => void;
  isDrilledInto?: (node: TNode) => boolean;
};

export type PopoverComponentProps = {
  node: NodeApi<TreeData>;
};

export type NodeProps = {
  node: NodeApi<TreeData>;
  className?: string;
  selected: boolean;
  halfCheck: boolean;
  disabled?: boolean;
  isParentDisabled?: boolean;
  onToggle: (id: string) => void;
  drillMode?: DrillMode;
  hideExpandIcon?: boolean;
  PopoverComponent?: React.ForwardRefExoticComponent<
    PopoverComponentProps & React.RefAttributes<HTMLDivElement>
  >;
  showAdminId?: boolean;
  showNodeCount?: boolean;
};

const iconProps = {
  'aria-label': 'node toggle button',
  boxSize: 6,
  color: 'text.primary',
  marginTop: '-2px',
  outline: 'none',
};

export const Node = ({
  node,
  className,
  selected,
  style,
  halfCheck,
  disabled,
  isParentDisabled,
  onToggle,
  drillMode,
  PopoverComponent,
  hideExpandIcon = false,
  showAdminId = false,
  showNodeCount = true,
}: NodeRendererProps<TreeData> & NodeProps) => {
  const isDrillModeEnabled = drillMode?.isEnabled;
  const isDrilledInto = drillMode?.isDrilledInto?.(node);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  const handleClickExpandNode: MouseEventHandler<SVGElement> = (e) => {
    e.stopPropagation();
    node.toggle();
  };

  const handleClickNodeDrill: MouseEventHandler<SVGElement> = (e) => {
    e.stopPropagation();
    if (isDrilledInto) {
      drillMode?.drillOutToLevel?.(drillMode.level);
    } else {
      drillMode?.drillIntoNode?.({ event: e, node, level: drillMode.level });
    }
  };

  const handleClickNode = (event: React.MouseEvent) => {
    event.stopPropagation();

    // Check if click was inside the Popover using ref
    if (popoverRef.current?.contains(event.target as Node)) {
      return;
    }

    // Expand/collapse parent nodes when clicking anywhere on the row.
    // Skip when drillMode is provided — popout trees use drill navigation instead.
    if (!drillMode) {
      const hasChildNodes = node.children && node.children.length > 0;
      if (hasChildNodes) {
        node.toggle();
      }
    }

    !disabled && onToggle(node.id);
    drillMode?.drillOutToLevel?.(drillMode.level);
  };

  const searchTerm = node?.tree?.searchTerm;
  const displaySuggestedTitles = resolveSuggestedTitles(node.data);
  const isSuggestedResult =
    searchTerm &&
    !isDirectMatch(node, searchTerm) &&
    isSuggestedMatch(node, searchTerm);
  const fullAdminIdText =
    node.data.id !== undefined && node.data.id !== null
      ? String(node.data.id)
      : '';
  const displayAdminIdText = getDisplayIdFromNodeId(fullAdminIdText);
  const [isAdminIdCopied, setIsAdminIdCopied] = React.useState(false);
  const copyTimeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const formattedCount = formatNodeCount(node?.data?.nodeCount ?? 0);

  const hasChildren = node.children && node.children.length > 0;
  const isTopLevel = node.data.depth === 0;
  const handleCopyAdminId = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (!fullAdminIdText) return;
    const copied = await copyTextToClipboard(fullAdminIdText);
    if (!copied) return;
    setIsAdminIdCopied(true);
    if (copyTimeoutRef.current !== null) {
      window.clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = window.setTimeout(() => {
      setIsAdminIdCopied(false);
    }, 1200);
  };
  return (
    <Flex
      data-testid="selection-tree-data-node"
      data-tree-row
      data-id={node.id}
      data-option-value={node.data.id}
      data-option-label={node.data.name}
      {...(!isDrillModeEnabled && { style })}
      className={classNames(
        `selection-tree-data-node-${kebabCase(node.data.name)}`,
        styles.nodeContainer,
        isDrilledInto && styles.drilledInto,
        className
      )}
      onClick={(event) => handleClickNode(event)}
      role="group"
    >
      {!isDrillModeEnabled && !hideExpandIcon && (
        <Icon
          {...(isParentDisabled || isTopLevel
            ? { display: hasChildren ? 'block' : 'none' }
            : { visibility: hasChildren ? 'visible' : 'hidden' })}
          {...iconProps}
          data-testid="popout-menu"
          as={node.isOpen ? ChevronDownIcon : ChevronRightIcon}
          onClick={handleClickExpandNode}
        />
      )}

      <Checkbox
        isChecked={selected}
        isIndeterminate={halfCheck && !selected}
        colorScheme="green"
        variant="node-checkbox"
        width="100%"
        disabled={disabled}
        className={styles.checkbox}
        data-testid={`checkbox-${kebabCase(node.data.name)}`}
      >
        <Flex
          alignItems="center"
          width="100%"
          overflow="visible"
          justifyContent="space-between"
        >
          <Flex
            alignItems="center"
            overflow="hidden"
            justifyContent="flex-start"
            flex="1"
            minWidth={0}
          >
            <Flex
              alignItems="center"
              overflow="hidden"
              flex="0 1 auto"
              minWidth={0}
              maxWidth="100%"
            >
              <div title={node.data.name} className={styles.label}>
                {node.data.name}
                {isSuggestedResult && (
                  <span className={styles.suggestedResult}> (suggested)</span>
                )}
              </div>
              {(displaySuggestedTitles?.length || node.data.info) && (
                <Flex display="none" _groupHover={{ display: 'flex' }}>
                  {node.data.info && (
                    <TreeTooltip label={node.data.info} mt="1px" />
                  )}
                  {displaySuggestedTitles &&
                    displaySuggestedTitles.length > 0 && (
                      <TreeTooltip
                        label={
                          <div>
                            <span className={styles.tooltipBold}>
                              Raw Titles:{' '}
                            </span>
                            {displaySuggestedTitles.slice(0, 7).join(', ')}.
                          </div>
                        }
                        mt="1px"
                      />
                    )}
                </Flex>
              )}
            </Flex>
            {PopoverComponent && (
              <PopoverComponent node={node} ref={popoverRef} />
            )}
          </Flex>
          <Flex alignItems="center" flexShrink={0} gap="6px">
            {showAdminId && displayAdminIdText.length > 0 && (
              <button
                type="button"
                className={classNames(
                  styles.adminId,
                  isAdminIdCopied && styles.adminIdCopied
                )}
                onClick={handleCopyAdminId}
                title={`Copy id: ${fullAdminIdText}`}
                data-copied={isAdminIdCopied ? 'true' : 'false'}
              >
                {isAdminIdCopied ? 'copied' : `id: ${displayAdminIdText}`}
              </button>
            )}
            {showNodeCount && !isNil(node?.data?.nodeCount) && (
              <Flex justifyContent="center" flexShrink={0}>
                <Tag
                  size="xs"
                  borderRadius="100"
                  padding="0 6px"
                  variant="subtle"
                  margin={0}
                  className={styles.countBadge}
                >
                  <Text
                    fontSize="10"
                    justifyContent="center"
                    className={styles.countBadgeText}
                  >
                    {formattedCount}
                  </Text>
                </Tag>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Checkbox>
      {isDrillModeEnabled && node.children && node.children.length > 0 && (
        <Icon
          {...iconProps}
          data-testid="popout-menu"
          as={ChevronRightIcon}
          onClick={handleClickNodeDrill}
          className={styles.drillIcon}
        />
      )}
    </Flex>
  );
};
